import React, {
    useState,
    useEffect,
    useContext,
    useCallback,
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
    Button,
    ModalNext,
    ModalHeader,
    ModalBody,
    ModalFooter,
    useModal,
} from '@jutro/components';
import { useTranslator } from '@jutro/locale';
import { ViewModelForm, useDataRefresh } from 'gw-portals-viewmodel-react';
import { useValidation } from 'gw-portals-validation-react';
import { ServiceRequestService } from 'gw-capability-servicerequest';
import messages from './UpdateAmountServicePopup_AX.messages';
import metadata from './UpdateAmountServicePopup_AX.metadata.json5';

const UpdateAmountServicePopupAX = (props) => {
    const { refreshData } = useDataRefresh();
    const { isComponentValid, onValidate } = useValidation('UpdateAmountServiceAX');
    const {
        isOpen,
        onReject,
        onResolve,
        onAfterClose,
        serviceRequestNumber,
        authHeader,
        viewModelService
    } = props;
    const {
        showAlert
    } = useModal();
    const translator = useTranslator();

    const [availableValues, setAvailableValues] = useState([]);
    const [serviceAmountVM, setServiceAmountVM] = useState({});

    useEffect(() => {
            const currencyTypes = [];
            _.filter(viewModelService.productMetadata.get('cc')
                .types.getTypelist('Currency').codes,
            (elt) => elt.code === 'mxn' || elt.code === 'usd').forEach((typecode) => {
                currencyTypes.push(_.merge({}, typecode, {
                    id: typecode.code,
                    displayName: translator({
                        id: `typekey.Currency.${typecode.code}`,
                        defaultMessage: 'Not found'
                    })
                }));
            });
            setAvailableValues(currencyTypes);
            setServiceAmountVM(
                viewModelService.create(
                    {
                        serviceRequestNumber: serviceRequestNumber,
                        amount: null,
                        currency: null
                    },
                    'cc',
                    'edge.capabilities.servicerequest.dto.AxUpdateAmountRequest'
                )
            ); 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewModelService]);

    const handleSend = useCallback(async () => {
        const updateAmountService = await ServiceRequestService.axUpdateAmountService(
            serviceAmountVM.value,
            authHeader
        ).catch((error) => {
            // eslint-disable-next-line no-console
            console.log(error);
            showAlert({
                title: messages.error,
                message: messages.errorMessage,
                status: 'error',
                icon: 'mi-error-outline'
            });
        });
        onResolve(updateAmountService);
    }, [serviceAmountVM, authHeader, onResolve]);

    const handleClose = useCallback(() => {
        onReject();
    }, [onReject]);

    const handleCurrencyChange = useCallback((option) => {
        _.set(serviceAmountVM, 'currency', option.id);
        refreshData();
    }, [serviceAmountVM, refreshData]);

    const handleServiceAmountVMChange = useCallback((newModel) => {
        setServiceAmountVM(newModel);
        refreshData();
    }, [refreshData]);

    if (_.isEmpty(serviceAmountVM)) {
        return null;
    }

    const overrides = {
        currencyTypeField: {
            availableValues: availableValues,
            showRequired: true
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            onValidate: onValidate,
            handleCurrencyChange: handleCurrencyChange
        }
    };

    return (
        <ModalNext isOpen={isOpen} onRequestClose={handleClose} onAfterClose={onAfterClose}>
            <ModalHeader title={translator(messages.addServiceAmountTitle)} />
            <ModalBody>
                <ViewModelForm
                    uiProps={metadata.componentContent.content}
                    model={serviceAmountVM}
                    onValidationChange={onValidate}
                    onModelChange={handleServiceAmountVMChange}
                    overrideProps={overrides}
                    callbackMap={resolvers.resolveCallbackMap}
                />
            </ModalBody>
            <ModalFooter>
                <Button
                    type="outlined"
                    onClick={handleClose}
                >
                    {translator(messages.cancel)}
                </Button>
                <Button
                    onClick={handleSend}
                    disabled={!isComponentValid}
                >
                    {translator(messages.send)}
                </Button>
            </ModalFooter>
        </ModalNext>
    );
};

UpdateAmountServicePopupAX.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onResolve: PropTypes.func.isRequired,
    onReject: PropTypes.func.isRequired,
    onAfterClose: PropTypes.func.isRequired,
    serviceRequestNumber: PropTypes.string.isRequired,
    authHeader: PropTypes.string,
};

UpdateAmountServicePopupAX.defaultProps = {
    authHeader: undefined
};

export default UpdateAmountServicePopupAX;
